import { useEffect, useState, useMemo, useCallback } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import {
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  Button,
  Input,
  Collapse,
} from 'reactstrap';

const PropertySelector = ({
  propertyList,
  selectedFilters,
  onPropertySelectionChange,
  onExportExcel,
  onBuildingTypeChange,
  isLoading,
  user,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const currentProperty = useMemo(
    () => propertyList.find((prop) => prop._id === selectedFilters.property_id),
    [propertyList, selectedFilters.property_id]
  );

  const buildingList = useMemo(() => {
    if (!currentProperty?.building) return [];
    return currentProperty.building.filter((b) => b && b.buildingName);
  }, [currentProperty]);

  const propertyOptions = useMemo(
    () =>
      propertyList.map((prop) => ({
        value: prop._id,
        label: `${prop.title}, ${prop.location || 'No location'}`,
      })),
    [propertyList]
  );

  const buildingOptions = useMemo(
    () =>
      buildingList.map((b) => ({
        value: b.buildingName,
        label: `${b.buildingName}${b.type ? ` (${b.type})` : ''}`,
        type: b.type || 'Apartment',
      })),
    [buildingList]
  );

  const selectedPropertyOption = useMemo(
    () => propertyOptions.find((opt) => opt.value === selectedFilters.property_id) || null,
    [propertyOptions, selectedFilters.property_id]
  );

  const selectedBuildingOption = useMemo(
    () => buildingOptions.find((opt) => opt.value === selectedFilters.building_name) || null,
    [buildingOptions, selectedFilters.building_name]
  );

  useEffect(() => {
    if (buildingList.length === 1 && !selectedFilters.building_name && selectedFilters.property_id) {
      const singleBuilding = buildingList[0];
      onPropertySelectionChange({
        ...selectedFilters,
        building_name: singleBuilding.buildingName,
      });
      if (onBuildingTypeChange) {
        onBuildingTypeChange(singleBuilding.type || 'Apartment');
      }
    }
  }, [buildingList, selectedFilters.property_id, selectedFilters.building_name, onPropertySelectionChange, onBuildingTypeChange]);

  useEffect(() => {
    if (!currentProperty || !selectedFilters.property_id) return;
    const updated = {
      ...selectedFilters,
      address: currentProperty.address || selectedFilters.address || '',
      postal_code: currentProperty.postal_code || selectedFilters.postal_code || '',
    };
    if (
      updated.address !== selectedFilters.address ||
      updated.postal_code !== selectedFilters.postal_code
    ) {
      onPropertySelectionChange(updated);
    }
  }, [currentProperty, selectedFilters, onPropertySelectionChange]);

  useEffect(() => {
    if (selectedBuildingOption && onBuildingTypeChange) {
      onBuildingTypeChange(selectedBuildingOption.type || 'Apartment');
    }
  }, [selectedBuildingOption, onBuildingTypeChange]);

  const handlePropertyChange = useCallback(
    (selectedOption) => {
      const newId = selectedOption ? selectedOption.value : '';
      const prop = propertyList.find((p) => p._id === newId);
      onPropertySelectionChange({
        ...selectedFilters,
        property_id: newId,
        building_name: '',
        address: prop?.address || '',
        postal_code: prop?.postal_code || '',
        unit_number: '',
        type: '',
        overall_status: '',
      });
      if (onBuildingTypeChange) {
        onBuildingTypeChange('Apartment');
      }
    },
    [propertyList, selectedFilters, onPropertySelectionChange, onBuildingTypeChange]
  );

  const handleBuildingChange = useCallback(
    (selectedOption) => {
      onPropertySelectionChange({
        ...selectedFilters,
        building_name: selectedOption ? selectedOption.value : '',
      });
      if (onBuildingTypeChange) {
        onBuildingTypeChange(selectedOption?.type || 'Apartment');
      }
    },
    [selectedFilters, onPropertySelectionChange, onBuildingTypeChange]
  );

  const handleDateChange = useCallback(
    (key) => (e) => {
      onPropertySelectionChange({
        ...selectedFilters,
        [key]: e.target.value || '',
      });
    },
    [selectedFilters, onPropertySelectionChange]
  );

  const toggleFilters = () => setIsFiltersOpen((prev) => !prev);

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, { isDisabled }) => ({
      ...base,
      zIndex: 2,
      backgroundColor: isDisabled ? '#f8f9fa' : base.backgroundColor,
      cursor: isDisabled ? 'not-allowed' : 'default',
    }),
  };

  const isExportDisabled = useMemo(() => {
    if (!selectedFilters.property_id) return true;
    if (buildingList.length > 0 && !selectedFilters.building_name) return true;
    return false;
  }, [selectedFilters.property_id, selectedFilters.building_name, buildingList.length]);

  const currentBuildingType = selectedBuildingOption?.type || 'Apartment';

  return (
    <Form className="mb-4">
      <Row className="g-3 align-items-end">
        <Col md={4}>
          <FormGroup>
            <Label className="fw-semibold">Property</Label>
            <Select
              value={selectedPropertyOption}
              onChange={handlePropertyChange}
              options={propertyOptions}
              placeholder="Select Property"
              isClearable
              isDisabled={isLoading}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </FormGroup>
        </Col>

        {buildingList.length > 0 && (
          <Col md={4}>
            <FormGroup>
              <Label className="fw-semibold">
                Building Name
                {selectedBuildingOption?.type && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 20,
                      backgroundColor: selectedBuildingOption.type === 'Townhouse' ? '#E8F4FD' : '#E8F5EE',
                      color: selectedBuildingOption.type === 'Townhouse' ? '#1A6B9A' : '#1A6B4A',
                      border: `1px solid ${selectedBuildingOption.type === 'Townhouse' ? '#B3D9F0' : '#B3E0CB'}`,
                    }}
                  >
                    {selectedBuildingOption.type}
                  </span>
                )}
              </Label>
              <Select
                value={selectedBuildingOption}
                onChange={handleBuildingChange}
                options={buildingOptions}
                placeholder="Select Building"
                isClearable={buildingList.length > 1}
                isDisabled={buildingList.length === 1 || isLoading}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>
        )}

        <Col md={buildingList.length > 0 ? 4 : 8}>
          <div
            className="d-flex align-items-center gap-2 flex-wrap"
            style={{ justifyContent: 'flex-end', height: '100%' }}
          >
            <div className="d-flex gap-2">
              <Button
                color="success"
                className="flex-fill"
                onClick={onExportExcel}
                disabled={isExportDisabled || isLoading}
                style={{ minWidth: '140px' }}
              >
                <FeatherIcon icon="download" size={18} className="me-2" />
                Export Excel
              </Button>
            </div>
            <Button
              color="primary"
              outline
              onClick={toggleFilters}
              disabled={isLoading}
              style={{ minWidth: '140px' }}
            >
              <FeatherIcon icon="filter" size={18} className="me-2" />
              More Filters
            </Button>
          </div>
        </Col>
      </Row>

      <Collapse isOpen={isFiltersOpen}>
        <Row className="g-3 mt-3">
          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">From Date</Label>
              <Input
                type="date"
                value={selectedFilters.startDate || ''}
                onChange={handleDateChange('startDate')}
                disabled={isLoading}
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">To Date</Label>
              <Input
                type="date"
                value={selectedFilters.endDate || ''}
                onChange={handleDateChange('endDate')}
                disabled={isLoading}
                min={selectedFilters.startDate || undefined}
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">Overall Status</Label>
              <Select
                value={
                  selectedFilters.overall_status
                    ? {
                      value: selectedFilters.overall_status,
                      label: selectedFilters.overall_status === 'passed' ? 'Passed' : 'Has Issues',
                    }
                    : null
                }
                onChange={(opt) =>
                  onPropertySelectionChange({
                    ...selectedFilters,
                    overall_status: opt?.value || '',
                  })
                }
                options={[
                  { value: 'passed', label: 'Passed' },
                  { value: 'has issues', label: 'Has Issues' },
                ]}
                placeholder="Filter by status..."
                isClearable
                isDisabled={isLoading}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup>
              <Label className="fw-semibold">Unit Number (partial match)</Label>
              <Input
                type="text"
                value={selectedFilters.unit_number || ''}
                onChange={(e) =>
                  onPropertySelectionChange({
                    ...selectedFilters,
                    unit_number: e.target.value.trim(),
                  })
                }
                placeholder="e.g. 101, 12A"
                disabled={isLoading}
              />
            </FormGroup>
          </Col>
        </Row>
      </Collapse>
    </Form>
  );
};

export { PropertySelector };